import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserIdentity } from '@lucid/portal-core';
import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import { partnerUserLocationSchema } from '../onboarding/validators';
import type { PartnerUserLocation } from '../onboarding/dto.onboarding';
import type { UserIdentity } from '../user-identity/dto.user-identity';

/** Validates each raw record individually so one malformed location doesn't drop the whole list. */
const parseLocations = (rawData: unknown): PartnerUserLocation[] => {
  const rawLocations = Array.isArray(rawData) ? rawData : [];
  return rawLocations.reduce<PartnerUserLocation[]>((acc, rawLocation) => {
    const result = partnerUserLocationSchema.safeParse(rawLocation);
    if (result.success) {
      acc.push(result.data);
    } else {
      console.error('Skipping invalid location record:', result.error);
    }
    return acc;
  }, []);
};

/*
|--------------------------------------------------------------------------
| GET LOCATIONS
|--------------------------------------------------------------------------
*/

type GetLocationsResponse = {
  data: unknown[];
};

/**
 * Fetches all organization locations from the backend API.
 * Uses authenticated user's Auth0 ID (automatically injected via LuppApi interceptor).
 *
 * @returns {UseQueryResult} React Query result with location data, loading state, and error
 *
 * @example
 * const { data, isLoading, error } = useGetLocations();
 */
export const useGetLocations = () => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.PARTNER_LOCATIONS],
    queryFn: async () => {
      const response = await LuppApi.get<GetLocationsResponse>(
        `${LUPP_API_BASE_PATH}/partners/locations`,
        {
          extractData: (response) => response, // Return full response
        }
      );

      return { items: parseLocations(response?.data) };
    },
  });
};

/**
 * Organization locations narrowed to the ones the signed-in user may assign prospects to, as
 * listed on their `/me` identity (an organization-wide Admin's identity lists every location).
 * The prospects API rejects any other location, so only these are offered in pickers.
 *
 * @returns {UseQueryResult} Same shape as {@link useGetLocations}, with `data.items` filtered
 *
 * @example
 * const { data } = useGetUserLocations();
 */
export const useGetUserLocations = () => {
  const query = useGetLocations();
  const { userIdentity } = useUserIdentity();
  // portal-core types only the common identity fields; `locations` comes from the raw /me payload.
  const userLocations = (userIdentity as unknown as Pick<UserIdentity, 'locations'> | null | undefined)
    ?.locations;

  const data = useMemo(() => {
    if (!query.data) {
      return query.data;
    }
    const allowedIds = new Set((userLocations ?? []).map((location) => location.locationIdentifier));
    return { items: query.data.items.filter((location) => allowedIds.has(location.locationIdentifier)) };
  }, [query.data, userLocations]);

  return { ...query, data };
};

/*
|--------------------------------------------------------------------------
| UPDATE USER LOCATIONS
|--------------------------------------------------------------------------
*/

type UpdateUserLocationsRequest = {
  business_partner_user_identifier: string;
  location_identifiers: string[];
};

type UpdateUserLocationsResponse = {
  data: unknown[];
};

export type UpdateUserLocationsParams = {
  organizationIdentifier: string;
  userId: string;
  locationIdentifiers: string[];
};

/**
 * Updates the set of locations assigned to a partner user. Sends the full
 * desired set of location identifiers (not a diff) and returns the resulting
 * list of assigned locations from the backend.
 *
 * @example
 * const { mutate } = useUpdateUserLocations();
 * mutate({ organizationIdentifier, userId, locationIdentifiers: ['loc-1', 'loc-2'] });
 */
export const useUpdateUserLocations = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      organizationIdentifier,
      userId,
      locationIdentifiers,
    }: UpdateUserLocationsParams) => {
      const response = await LuppApi.patch<UpdateUserLocationsRequest, UpdateUserLocationsResponse>(
        `${LUPP_API_BASE_PATH}/partners/${organizationIdentifier}/user-locations`,
        {
          business_partner_user_identifier: userId,
          location_identifiers: locationIdentifiers,
        },
        {
          extractData: (response) => response, // Return full response
        }
      );

      return parseLocations(response?.data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.PARTNER_USERS] });
    },
    onError: (error: Error) => {
      console.error('Failed to update user locations:', error.message);
    },
  });
};
